"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, ChevronRight, Cpu, Layers, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
export default function PatGrid() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const sideCards = [{
    id: "bruker-pat",
    title: "Bruker PAT Spectrometer Family",
    badge: "Spectroscopy Systems",
    desc: "High-stability FT-NIR process analysers including MATRIX-F II and BEAM for real-time blend uniformity, reaction monitoring, and continuous processing.",
    href: "/UserPatPage",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/bruker_pat_spectrometer.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/bruker_pat_spectrometer.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/bruker_pat_spectrometer.png-wvc-sizes",
    features: ["Multipoint fiber optic probes", "ATEX / IECEx compliant builds"]
  }, {
    id: "optical-flow-cells",
    title: "Industrial Optical Flow Cell Assemblies",
    badge: "In-Line Measurement",
    desc: "Robust flow cells engineered for harsh process lines, high-pressure liquid streams, and sanitary biopharma manufacturing interfaces.",
    href: "/ProcessAnalyticalTechnologyPage",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/optical_flow_cell_assembly.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/optical_flow_cell_assembly.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/optical_flow_cell_assembly.png-wvc-sizes",
    features: ["Hastelloy & 316L SS options", "CIP / SIP sanitary design"]
  }];
  return <section id="patsolutionsfeaturedgrid" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 text-primary font-medium text-sm mb-3">
              <Cpu className="size-4" />
              <span>Analytical & Process Integration</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
              Process Analytical Technology Solutions
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              Real-time quality verification and closed-loop process monitoring designed for regulated pharmaceutical, chemical, and biopharma facilities.
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild variant="outline" className="group">
              <Link to="/ProcessAnalyticalTechnologyPage">
                <span>View All PAT Solutions</span>
                <ChevronRight className="size-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>

        {/* 8/4 Grid Split */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
          {/* Main 8-Column Featured Card */}
          <motion.div variants={itemVariants} className="lg:col-span-8 flex">
            <Card className="w-full relative overflow-hidden border border-border bg-card shadow-md flex flex-col justify-between group hover:shadow-lg transition-[box-shadow,border-color] duration-300">
              <div className="p-6 sm:p-8 md:p-10 flex flex-col h-full justify-between z-10">
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <Badge variant="default" className="bg-primary text-primary-foreground font-semibold px-3 py-1">
                      Featured Hub
                    </Badge>
                    <Badge variant="secondary" className="bg-secondary text-secondary-foreground font-normal">
                      Full-Lifecycle Support
                    </Badge>
                  </div>

                  <h3 className="text-2xl sm:text-3xl font-bold text-card-foreground tracking-tight max-w-xl">
                    Process Analytical Technology (PAT) Hub
                  </h3>

                  <p className="mt-4 text-muted-foreground text-base sm:text-lg leading-relaxed max-w-xl">
                    Connect spectrometer hardware, industrial probe interfaces, and chemometric data models into continuous manufacturing lines with full qualification and compliance support.
                  </p>

                  {/* Bullet points */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mt-6 max-w-xl">
                    <div className="flex items-start gap-2.5">
                      <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground font-medium">In-line & on-line NIR & Raman</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground font-medium">GMP & 21 CFR Part 11 compliant</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground font-medium">Blend & drying endpoint control</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground font-medium">Method development & validation</span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                  <Button asChild size="lg" className="shadow-sm">
                    <Link to="/ProcessAnalyticalTechnologyPage">
                      <span>Explore PAT Solutions Hub</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>

                  <Button asChild variant="ghost" className="text-muted-foreground hover:text-foreground">
                    <Link to="/DiscussYourApplicationPage">
                      <span>Discuss Your Application</span>
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Decorative Image Backdrop / Right Banner */}
              <div className="relative w-full h-56 sm:h-64 lg:h-72 mt-4 lg:mt-0 overflow-hidden border-t border-border bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/pat_solutions_hub_featured.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/pat_solutions_hub_featured.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/pat_solutions_hub_featured.png-wvc-sizes" alt="Process Analytical Technology monitoring system" loading="lazy" decoding="async" className="w-full h-full object-cover object-center group-hover:scale-[1.02] transition-transform duration-500" />
                <div className="absolute inset-0 bg-linear-to-t from-card/80 via-transparent to-transparent pointer-events-none" />
              </div>
            </Card>
          </motion.div>

          {/* Side 4-Column Stack */}
          <div className="lg:col-span-4 flex flex-col gap-6">
            {sideCards.map((card, i) => <motion.div key={card.id} variants={itemVariants} className="flex-1 flex" data-index={i}>
                <Card className="w-full border border-border bg-card shadow-xs flex flex-col justify-between overflow-hidden group hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-300">
                  <div className="relative h-44 w-full overflow-hidden bg-muted">
                    <img src={card.image} data-wvc-srcset={card.srcset} data-wvc-sizes={card.sizes} alt={card.title} loading="lazy" decoding="async" className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-3 left-3">
                      <Badge variant="secondary" className="bg-background/90 text-foreground font-medium shadow-xs">
                        {card.badge}
                      </Badge>
                    </div>
                  </div>

                  <CardContent className="p-5 sm:p-6 flex flex-col justify-between flex-1">
                    <div>
                      <h4 className="text-xl font-bold text-card-foreground tracking-tight group-hover:text-primary transition-colors">
                        {card.title}
                      </h4>
                      <p className="mt-2.5 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                        {card.desc}
                      </p>

                      <ul className="mt-4 space-y-1.5">
                        {card.features.map((feat, fIdx) => <li key={fIdx} className="text-xs text-foreground font-medium flex items-center gap-2">
                            <span className="size-1.5 rounded-full bg-primary" />
                            <span>{feat}</span>
                          </li>)}
                      </ul>
                    </div>

                    <div className="mt-6 pt-4 border-t border-border flex items-center justify-between">
                      <Button asChild variant="link" className="px-0 text-primary font-semibold group/link">
                        <Link to={card.href}>
                          <span>Learn more</span>
                          <ChevronRight className="size-4 ml-1 transition-transform group-hover/link:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}